import React from "react";
import { motion } from "framer-motion";
import { Award, Heart } from "lucide-react";

const CREDENTIALS = [
  "Registered Nurse · AHPRA NMW0002614487",
  "Cosmetic Dermal Science",
  "Laser Certified",
  "Women's Health",
  "Weight-Loss Consultant",
];

// Shawnna's portrait
const PORTRAIT_IMG =
  "https://customer-assets.emergentagent.com/job_derma-studio-2/artifacts/h1twv7n0_IMG_0514.jpeg";

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#F5EFEB]"
    >
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        {/* Left: portrait + credentials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-5 relative"
        >
          <div className="aspect-[4/5] overflow-hidden rounded-sm bg-[#F5EFEB]">
            <img
              src={PORTRAIT_IMG}
              alt="Shawnna — Registered Nurse and Owner of Derma Aesthetic Studio"
              data-testid="about-portrait"
              className="w-full h-full object-cover object-[center_15%]"
            />
          </div>

          {/* floating credential card */}
          <motion.div
            initial={{ opacity: 0, x: -20, y: 20 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="hidden md:block absolute -bottom-8 -right-8 bg-[#FDFBF7] border border-[#E8DFD8] p-6 max-w-[280px] shadow-sm"
            data-testid="about-ahpra-card"
          >
            <Award size={20} strokeWidth={1.25} className="text-[#C89F95] mb-3" />
            <p className="eyebrow text-[#C89F95] mb-2">AHPRA Registered</p>
            <p className="font-serif text-base leading-snug text-[#2D2926]">
              NMW0002614487
            </p>
          </motion.div>
        </motion.div>

        {/* Right: story */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <p className="eyebrow mb-6">Meet Your Nurse</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight tracking-tight text-[#2D2926] mb-3">
            Shawnna
          </h2>
          <p className="eyebrow text-[#C89F95] mb-10">
            Registered Nurse · Owner of Derma Aesthetic Studio
          </p>

          <div className="space-y-6 text-[#5C5753] leading-relaxed font-light text-base md:text-lg max-w-2xl">
            <p>
              Shawnna's practice is grounded in helping women feel
              <em className="text-[#2D2926]"> radiant, confident</em> and completely at ease in their
              own skin. Treatments are never rushed — and never one-size-fits-all.
            </p>
            <p>
              With a background spanning <em className="text-[#2D2926]">personal training and
              nutritional coaching</em>, mental-health community nursing, and now a nurse-led
              aesthetic studio for integrated health and skin care, she brings a rare,
              whole-person perspective to every appointment.
            </p>
            <p>
              By combining advanced skin therapies with a deep understanding of how skin
              evolves through life, stress and hormonal change, this is aesthetic care
              designed to <em className="text-[#2D2926]">enhance, restore and support</em> —
              never to overcorrect.
            </p>
            <p>
              A place where natural results, clinical expertise and genuine care come
              together beautifully.
            </p>
          </div>

          {/* credentials list */}
          <div className="mt-12 pt-10 border-t border-[#E8DFD8]">
            <p className="eyebrow mb-6">Qualifications &amp; Credentials</p>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="about-credentials">
              {CREDENTIALS.map((c) => (
                <li
                  key={c}
                  className="flex items-start gap-3 text-sm text-[#2D2926] font-light"
                >
                  <span className="text-[#C89F95] mt-1.5 shrink-0">·</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>

          <a
            href="#book"
            data-testid="about-cta"
            className="inline-flex items-center gap-2 mt-12 bg-[#C89F95] hover:bg-[#B3867B] text-white px-8 py-4 rounded-sm text-xs uppercase tracking-[0.24em] transition-colors"
          >
            <Heart size={14} strokeWidth={1.5} />
            Begin your personalised consultation
          </a>
        </motion.div>
      </div>
    </section>
  );
}
